<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <template v-if="route.meta.layout === 'auth'">
      <router-view></router-view>
    </template>
    <template v-else>
      <Navbar 
        @toggle-sidebar="toggleSidebar" 
        :is-mobile="isMobile" 
        class="h-16 fixed top-0 left-0 right-0 z-50" 
      />
      <div class="flex min-h-screen pt-16">
        <!-- Sidebar desktop -->
        <Sidebar 
          v-if="isAuthenticated && !isMobile" 
          v-model="isSidebarCollapsed" 
        />
        <!-- Sidebar drawer mobile -->
        <transition name="fade">
          <div 
            v-if="isAuthenticated && isMobile && isSidebarOpen" 
            class="fixed inset-0 z-40 flex"
          >
            <div class="fixed inset-0 bg-black bg-opacity-50" @click="isSidebarOpen = false"></div>
            <Sidebar v-model="isSidebarCollapsed" class="relative z-50 w-64" />
          </div>
        </transition>
        <div 
          class="flex-1 transition-all duration-300 min-w-0" 
          :class="{
            'ml-64': !isSidebarCollapsed && isAuthenticated && !isMobile,
            'ml-16': isSidebarCollapsed && isAuthenticated && !isMobile
          }"
        >
          <router-view></router-view>
        </div>
      </div>
    </template>
    <MobileNavBar v-if="isMobile && isAuthenticated" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/themeStore'
import { storeToRefs } from 'pinia'
import Navbar from '@/components/Navbar.vue'
import Sidebar from '@/components/layout/Sidebar.vue'
import MobileNavBar from '@/components/layout/MobileNavBar.vue'

const route = useRoute()
const authStore = useAuthStore()
const themeStore = useThemeStore()
const { isAuthenticated } = storeToRefs(authStore)
const { isDarkMode } = storeToRefs(themeStore)

// Responsive state
const isMobile = ref(false)
const isSidebarOpen = ref(false) // Pour le drawer mobile
const isSidebarCollapsed = ref(true) // Par défaut replié sur desktop

const checkMobile = () => {
  isMobile.value = window.innerWidth < 1024 // lg breakpoint Tailwind
  if (isMobile.value) {
    isSidebarOpen.value = false // Drawer fermé par défaut
  } else {
    isSidebarCollapsed.value = true // Sidebar repliée par défaut sur desktop
    isSidebarOpen.value = false
  }
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  
  // Vérifier l'authentification au chargement de l'application
  authStore.checkAuth()
})

// Nettoyage
onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const toggleSidebar = () => {
  if (isMobile.value) {
    isSidebarOpen.value = !isSidebarOpen.value
  } else {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
  }
}

// Appliquer la classe dark au body en fonction du thème
watch(() => isDarkMode.value, (newValue) => {
  if (newValue) {
    document.body.classList.add('dark', 'bg-gray-900', 'text-white')
  } else {
    document.body.classList.remove('dark', 'bg-gray-900', 'text-white')
  }
}, { immediate: true })

// Initialiser la classe du body au chargement
onMounted(() => {
  if (isDarkMode.value) {
    document.body.classList.add('dark', 'bg-gray-900', 'text-white')
  } else {
    document.body.classList.remove('dark', 'bg-gray-900', 'text-white')
  }
})
</script>

<style>
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}
</style>

